import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, Signal, untracked } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { devices, lensManufacturers } from '../../config/devices';
import {
    DATE_INVALID_FORMAT,
    DATE_PURCHASE_TOO_EARLY,
    DATE_PURCHASE_TOO_LATE,
    DATE_INVALID_DAY,
    DEVICE_TYPE_REQUIRED,
    PRICE_UP_TO_2500,
} from '@config/error-messages.const';
import { ElectronicsDevice } from '../../models/electronics-device';
import { InsuredObject } from '@models/insured-object';
import { OrderStepBaseComponent } from 'app/order/order-step-base-component';
import { OrderService } from 'app/order/state/order.service';
import { OrderState } from 'app/order/models/order-state.model';
import { CommonValidators } from 'app/shared/validators/common-validators';
import { ValidateIMEI } from 'app/shared/validators/imei.validator';
import { compareInsuredObjects } from 'app/utils/helper.functions';
import { addMonths, differenceInDays } from 'date-fns';
import { map, debounceTime, startWith } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { getOrderStateTag } from 'app/order/fns';
import { parseDateString, uniqueId } from '@co-3/ctrl-ui/common';
import { validateDate } from 'app/shared/helper-functions';
import { VariantsFieldsetComponent } from '../../components/variants-fieldset/variants-fieldset.component';
import { SelectWithOtherOptionComponent } from 'app/shared/select-with-other-option/select-with-other-option.component';
import { ErrorMessagesComponent } from 'app/shared/components/error-messages/error-messages.component';
import { FormLabelPipe } from 'app/shared/pipes/form-label.pipe';
import { InfoTooltipComponent } from 'app/shared/components/info-tooltip/info-tooltip.component';
import { NgClass } from '@angular/common';
import { RequiredInfoComponent } from 'app/shared/components/required-info/required-info.component';
import { InlinePriceDisplayComponent } from 'app/order/components/inline-price-display/inline-price-display.component';
import { NavigationButtons } from 'app/order/components/navigation-buttons/navigation-buttons.component';
import { ReactiveLabelDirective } from 'app/shared/directives/reactive-label.directive';
import { CtrlDateInputDirective } from '@co-3/ctrl-ui/forms';
import { PriceInputDirective } from 'app/shared/directives/price-input.directive';

@Component({
    selector: 'app-electronics-b2c-device',
    templateUrl: './electronics-b2c-device.component.html',
    styleUrl: './electronics-b2c-device.component.scss',
    inputs: ['state', 'isOpen', 'isFirstStep'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        VariantsFieldsetComponent,
        ReactiveFormsModule,
        SelectWithOtherOptionComponent,
        ErrorMessagesComponent,
        FormLabelPipe,
        InfoTooltipComponent,
        NgClass,
        RequiredInfoComponent,
        InlinePriceDisplayComponent,
        NavigationButtons,
        ReactiveLabelDirective,
        CtrlDateInputDirective,
        PriceInputDirective,
    ],
})
export class ElectronicsB2CDeviceComponent
    extends OrderStepBaseComponent<Pick<OrderState, 'insuredObjects'>>
    implements OnInit
{
    private formBuilder = inject(FormBuilder);
    private orderService = inject(OrderService);

    protected devices: ElectronicsDevice[] = [...devices];

    public lensManufacturers: string[] = [...lensManufacturers];
    public serial: 'Seriennummer' | 'IMEI' = 'Seriennummer';

    public form = this.formBuilder.group({
        device: this.formBuilder.group({
            type: [null as string | number, Validators.required],
            description: [null as string | number, Validators.required],
            detail: ['' as string | number],
            date: [null as string | number, CommonValidators.dateBetween(addMonths(new Date(), -6), new Date())],
            model: ['' as string | number, Validators.required],
            value: ['' as string | number, CommonValidators.priceBetween(1, 2500).concat(Validators.required)],
        }),
        lens: this.formBuilder.group({
            type: ['Objektiv' as string | number],
            description: [null as string | number, Validators.required],
            model: ['' as string | number, CommonValidators.something],
            value: ['' as string | number, CommonValidators.priceBetween(1, 2500).concat(Validators.required)],
            detail: ['' as string | number],
        }),
        toggleLens: [false],
    });
    // #region errormessages
    protected valueErrorMessages = {
        required: PRICE_UP_TO_2500,
        noValidPrice: PRICE_UP_TO_2500,
        priceTooLow: PRICE_UP_TO_2500,
        priceToHigh: PRICE_UP_TO_2500,
    };
    protected descriptionErrorMessage = computed(() => {
        if (this.isAppliance()) {
            return {
                required: 'Bitte gib den Gerätetyp an',
                minlength: 'Der Gerätetyp muss mindestens 2 Zeichen lang sein.',
            };
        }
        return {
            required: 'Bitte wähle einen Hersteller aus',
            minlength: 'Der Hersteller muss mindestens 2 Zeichen lang sein.',
        };
    });
    protected lensModelErrorMessages = {
        required: 'Bitte gib das Modell an.',
        minlength: 'Das Modell muss mindestens 3 Zeichen lang sein.',
    };
    protected typeErrorMessages = DEVICE_TYPE_REQUIRED;
    protected dateErrorMessages = {
        required: DATE_INVALID_FORMAT,
        dateTooEarly: DATE_PURCHASE_TOO_EARLY,
        dateTooLate: DATE_PURCHASE_TOO_LATE,
        invalidDay: DATE_INVALID_DAY,
        invalidDateFormat: DATE_INVALID_FORMAT,
    };
    protected detailErrorMessages = {
        notAnIMEI: 'Die IMEI ist 15 Zeichen lang und besteht nur aus Zahlen.',
        invalidIMEI: 'Bitte gib eine gültige IMEI an.',
    };
    // #endregion
    // #region errorIds
    protected deviceModelErrorId = uniqueId('device-model-error');
    protected deviceValueErrorId = uniqueId('device-value-error');
    protected dateErrorId = uniqueId('date-error');
    protected detailErrorId = uniqueId('detail-error');
    protected lensValueErrorId = uniqueId('lens-value-error');
    protected lensModelErrorId = uniqueId('lens-model-error');
    // #endregion

    private formValueChanges = toSignal(this.form.valueChanges.pipe(startWith(this.form.value)));
    private insuredObjectsForm = toSignal(
        this.form.valueChanges.pipe(
            startWith(this.form.value),
            debounceTime(300),
            map((value) =>
                this.insuredObjectsFromForm(value)
                    .map((insuredObject) => ({ ...insuredObject, value: insuredObject.value }))
                    .filter((insuredObject) => !!insuredObject.value),
            ),
        ),
    );
    private devicePurchaseDate = computed(() => this.formValueChanges().device.date);

    protected hideTypeSelector = computed(() => this.product()?.slug.startsWith('handyversicherung') ?? false);

    protected isAppliance = computed(() => this.formValueChanges().device?.type === 'Haushaltsgerät');

    protected hasDeductible = getOrderStateTag('hasDeductible') as Signal<boolean>;
    protected hasTheftCoverage = getOrderStateTag('hasTheftCoverage') as Signal<boolean>;
    protected hasMonthlySettlementPeriod = getOrderStateTag('hasMonthlySettlementPeriod') as Signal<boolean>;

    protected deviceType = computed(() => this.formValueChanges().device.type);

    protected manufacturers = computed(
        () => this.devices.find((item) => item.name === this.deviceType())?.manufacturers ?? [],
    );

    protected showLensForm = computed(() => this.deviceType() === 'Kamera' && !!this.formValueChanges().toggleLens);

    private insuredObjects = computed(() => this._state()?.insuredObjects || [], { equal: compareInsuredObjects });

    public constructor() {
        super();

        // update the serial number validator when device type changes
        effect(() => {
            const deviceDetail = this.form.controls.device.controls.detail;

            if (this.deviceType() === 'Smartphone') {
                deviceDetail.setValidators([ValidateIMEI]);
                this.serial = 'IMEI';
            } else {
                deviceDetail.setValidators([]);
                this.serial = 'Seriennummer';
            }
            deviceDetail.updateValueAndValidity();
        });

        // toggle the lens form when the device is a camera
        effect(() => {
            if (this.showLensForm() && this.form.controls.lens.disabled) {
                this.form.controls.lens.enable();
            } else if (this.form.controls.lens.enabled) {
                this.form.controls.lens.disable();
            }
        });

        // update form when state changes
        effect(() => {
            const insuredObjects = this.insuredObjects();

            if (insuredObjects.length > 0) {
                this.form.patchValue(
                    {
                        device: { ...insuredObjects[0] },
                        lens: {
                            ...insuredObjects[1],
                        },
                        toggleLens: !!insuredObjects[1],
                    },
                    { emitEvent: true },
                );
            } else {
                untracked(() => {
                    const deviceType = this.product()?.slug.startsWith('handyversicherung')
                        ? 'Smartphone'
                        : (this.form.controls.device.controls.type.value ?? null);

                    this.form.controls.device.controls.type.patchValue(deviceType);
                });
            }
        });

        // Update the boughtInLastTwoWeeks tag when the date change
        effect(() => {
            const devicePurchaseDate = this.devicePurchaseDate();
            if (typeof devicePurchaseDate === 'string' && validateDate(devicePurchaseDate)) {
                this.toggleBoughtInLastTwoWeeks(
                    differenceInDays(new Date(), parseDateString(devicePurchaseDate, 'dd.MM.yyyy')) < 15,
                );
            }
        });

        // Update the price when the form or one of the tags changes
        effect(() => {
            const insureObjects = this.insuredObjectsForm();
            this.hasDeductible();
            this.hasTheftCoverage();
            this.hasMonthlySettlementPeriod();

            untracked(() => this.priceService.updatePrice({ insuredObjects: insureObjects }));
        });

        // Reset the description and model when the device type switches to or from Appliance
        // The description field is used to hold the manufacturer or "device type 2", depending on the case
        // The model field is used to hold the model or brand, depending on the case
        effect(() => {
            this.isAppliance();
            untracked(() => {
                this.form.controls.device.controls.description.reset();
                this.form.controls.device.controls.model.reset();
            });
        });
    }

    public ngOnInit(): void {
        this.form.controls.lens.disable();
    }

    private insuredObjectsFromForm(value: {
        device?: Partial<InsuredObject>;
        lens?: Partial<InsuredObject>;
        toggleLens?: boolean;
    }): InsuredObject[] {
        const insuredObjects: InsuredObject[] = [{ ...value.device }];
        if (value.toggleLens && value.device.type === 'Kamera') {
            insuredObjects.push({ ...value.lens, date: value.device.date });
        }
        return insuredObjects;
    }

    public getData(): Pick<OrderState, 'insuredObjects'> {
        return {
            insuredObjects: this.insuredObjectsFromForm(this.form.getRawValue()).map((insuredObject) => ({
                ...insuredObject,
            })),
        };
    }

    protected toggleDeductible(deductible: boolean): void {
        this.orderService.setTag('hasDeductible', deductible);
    }

    protected toggleTheftCoverage(coverage: boolean): void {
        this.orderService.setTag('hasTheftCoverage', coverage);
    }

    protected toggleSettlementPeriod(monthly: boolean): void {
        this.orderService.setTag('hasMonthlySettlementPeriod', monthly);
    }

    protected toggleBoughtInLastTwoWeeks(boughtInLastTwoWeeks: boolean): void {
        this.orderService.setTag('boughtInLastTwoWeeks', boughtInLastTwoWeeks);
    }
}
